import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChampionshipsAPI, PlayersAPI } from "@/lib/api";
import { orderedSections } from "@/lib/sections";
import ShareStoryButton from "@/components/ShareStoryButton";
import SiteHeader from "@/components/SiteHeader";
import ScanReveal from "@/components/ScanReveal";
import GoldenParticles from "@/components/GoldenParticles";
import ImmersiveLightbox from "@/components/ImmersiveLightbox";
import { FullPageLoader } from "@/components/Loaders";
import { useAuth } from "@/contexts/AuthContext";
import { getSport, sportTerms } from "@/lib/sports";
import { themeVars, titleStyles } from "@/lib/appearance";
import ShareButton from "@/components/ShareButton";
import {
  ArrowLeft,
  Award,
  Crown,
  Handshake,
  Pencil,
  Shield,
  Sparkles,
  Users,
  Video,
} from "lucide-react";
import { isVideoFileUrl, toPlayableVideoUrl, videoPosterUrl } from "@/lib/video";
import { transparentLogoUrl } from "@/lib/logo";

const ease = [0.22, 1, 0.36, 1];

const HighlightEmbed = ({ h }) => {
  const url = h.url || "";
  let embedUrl = url;
  if (h.type === "youtube" || /youtube\.com|youtu\.be/.test(url)) {
    const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
    if (m) embedUrl = `https://www.youtube.com/embed/${m[1]}`;
  } else if (h.type === "vimeo" || /vimeo\.com/.test(url)) {
    const m = url.match(/vimeo\.com\/(\d+)/);
    if (m) embedUrl = `https://player.vimeo.com/video/${m[1]}`;
  }
  const isVideoFile = isVideoFileUrl(url, h.type);
  return (
    <div className="relative aspect-video w-full rounded-xl overflow-hidden border border-white/10 bg-black">
      {isVideoFile ? (
        <video src={toPlayableVideoUrl(url)} controls playsInline preload="metadata" className="w-full h-full object-cover" poster={h.poster || videoPosterUrl(url) || undefined} />
      ) : (
        <iframe
          src={embedUrl}
          title={h.title || "highlight"}
          className="w-full h-full"
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        />
      )}
    </div>
  );
};

const StatBox = ({ label, value, color }) => {
  const v = String(value ?? "");
  const sizeClass =
    v.length >= 8 ? "text-2xl sm:text-3xl md:text-4xl"
    : v.length >= 6 ? "text-3xl sm:text-4xl md:text-5xl"
    : "text-4xl sm:text-5xl md:text-5xl";
  return (
    <div className="bg-[#111216] border border-white/10 rounded-xl p-4 sm:p-5 md:p-6 min-w-0 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-1 h-full" style={{ background: color }} />
      <div className={`font-display ${sizeClass} font-black leading-none whitespace-nowrap text-white`}>
        {v}
      </div>
      <div className="font-display font-bold uppercase mt-2 text-[10px] sm:text-[11px] tracking-[0.18em] sm:tracking-[0.22em] leading-tight text-zinc-500">
        {label}
      </div>
    </div>
  );
};

const SectionHeading = ({ children, right }) => (
  <div className="flex items-end justify-between mb-6">
    <h2 className="font-display text-3xl md:text-5xl font-bold uppercase tracking-tight">
      {children}
    </h2>
    {right}
  </div>
);

const TeamBadge = ({ name, logoUrl, color, size = "md" }) => {
  const dims = size === "sm" ? "w-6 h-6 sm:w-8 sm:h-8" : "w-9 h-9 sm:w-11 sm:h-11";
  return (
    <div
      className={`${dims} rounded-full flex items-center justify-center shrink-0 overflow-hidden border`}
      style={{
        backgroundColor: `${color || "#444"}22`,
        borderColor: `${color || "#444"}55`,
      }}
    >
      {logoUrl ? (
        <img src={transparentLogoUrl(logoUrl)} alt={name} className="w-full h-full object-contain p-0.5" />
      ) : (
        <span className="font-display font-black text-[10px] sm:text-xs" style={{ color: color || "#fff" }}>
          {name?.[0]}
        </span>
      )}
    </div>
  );
};

export default function ChampionshipProfile() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [champ, setChamp] = useState(null);
  const [players, setPlayers] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    ChampionshipsAPI.get(slug)
      .then(async (c) => {
        if (!mounted) return;
        setChamp(c);
        if (c.player_ids?.length) {
          const loaded = await Promise.all(
            c.player_ids.map((id) => PlayersAPI.get(id).catch(() => null)),
          );
          if (mounted) setPlayers(loaded.filter(Boolean));
        }
      })
      .catch(() => mounted && setError("Championnat introuvable"))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [slug]);

  if (loading) return <FullPageLoader caption="Chargement du championnat…" />;

  if (error || !champ)
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        <SiteHeader />
        <div className="max-w-5xl mx-auto w-full px-4 md:px-8 py-24 text-center">
          <div className="font-display text-4xl font-semibold uppercase tracking-tight mb-3">
            Championnat introuvable
          </div>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-[#D4AF37] hover:underline"
            data-testid="back-championships"
          >
            <ArrowLeft className="w-4 h-4" /> Espace administrateur
          </Link>
        </div>
      </div>
    );

  const accent = champ.primary_color || "#D4AF37";
  const sportDef = getSport(champ.sport);
  const ap = champ.appearance || {};
  const terms = sportTerms(champ.sport);
  const titleCfg = titleStyles(ap, "xl");
  const standings = [...(champ.standings || [])].sort(
    (a, b) => (b.points || 0) - (a.points || 0),
  );
  const playerById = Object.fromEntries(players.map((p) => [p.id, p]));
  const statsEntries = sportDef.tournament_stats.filter(
    (s) => champ.stats?.[s.key] !== undefined,
  );
  const visibleStats = statsEntries.length > 0 ? statsEntries : sportDef.tournament_stats;
  const secOrder = orderedSections("championship", champ.section_order);
  const secStyle = (k) => ({ order: 10 + secOrder.indexOf(k) });

  return (
    <ScanReveal slug={champ.slug} kind="championship">
      <div className="event-theme min-h-screen bg-[#050505] text-white flex flex-col" style={themeVars(ap)}>
        <SiteHeader playerName={champ.name} accentColor={accent} />

        {/* ---------- Hero ---------- */}
        <section className="relative">
          <GoldenParticles count={10} density="subtle" />
          <div className="relative aspect-[16/11] sm:aspect-[16/8] md:aspect-[16/6] w-full overflow-hidden">
            {champ.cover_image_url ? (
              <img
                src={champ.cover_image_url}
                alt={champ.name}
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A]" />
            )}
            <div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(180deg, rgba(5,5,5,0.2) 30%, rgba(5,5,5,0.95) 100%)`,
              }}
            />
            <div className="absolute top-4 right-4 md:top-8 md:right-8 flex items-center gap-2">
              <ShareButton
                kind="championship"
                slug={champ.slug}
                title={champ.name}
                accent={accent}
                compact
              />
              {(user?.role === "admin" ||
                (user?.role === "organizer" && champ.organizer_id === user?.id)) && (
                <Link
                  to={`/admin/championships/${champ.id}`}
                  data-testid="edit-championship-link"
                  className="text-xs uppercase tracking-[0.2em] text-zinc-300 hover:text-white inline-flex items-center gap-1.5 backdrop-blur-md bg-black/30 border border-white/10 rounded-full px-3 py-1.5"
                >
                  <Pencil className="w-3 h-3" /> Éditer
                </Link>
              )}
            </div>
            <div className="absolute left-0 right-0 bottom-0 px-4 md:px-8 pb-8 md:pb-14">
              <div className="max-w-5xl mx-auto">
                <div className="flex items-end gap-4">
                  {champ.logo_url && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.85 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.6, ease }}
                      className="w-16 h-16 sm:w-24 sm:h-24 rounded-2xl overflow-hidden border border-white/15 bg-black/50 backdrop-blur-md p-2 shrink-0"
                      data-testid="championship-logo"
                    >
                      <img src={transparentLogoUrl(champ.logo_url)} alt="" className="w-full h-full object-contain" />
                    </motion.div>
                  )}
                  <div className="min-w-0">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6 }}
                      className="text-overline mb-2"
                      style={{ color: accent }}
                    >
                      <Crown className="w-3 h-3 inline mr-1" /> Championnat
                      {champ.season ? ` · Saison ${champ.season}` : ""}
                    </motion.div>
                    {!ap.hide_title && (
                      <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.85, delay: 0.08, ease }}
                        className={`font-display ${titleCfg.sizeClass} font-semibold uppercase tracking-tighter leading-[0.85] ${ap.title_color ? "" : "text-gradient-gold"}`}
                        style={{
                          ...(ap.title_color
                            ? { color: ap.title_color }
                            : { filter: "drop-shadow(0 6px 22px rgba(212,175,55,0.22))" }),
                          ...titleCfg.style,
                        }}
                        data-testid="championship-name"
                      >
                        {champ.name}
                      </motion.h1>
                    )}
                  </div>
                </div>
                {champ.subtitle && (
                  <p
                    className="font-body text-base md:text-xl text-zinc-300 mt-3 max-w-2xl"
                    style={ap.subtitle_color ? { color: ap.subtitle_color } : undefined}
                  >
                    {champ.subtitle}
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ---------- Présentation ---------- */}
        {champ.description && (
          <section
            className="max-w-5xl mx-auto w-full px-4 md:px-8 pt-12 md:pt-16"
            data-testid="championship-description"
            style={secStyle("description")}
          >
            <SectionHeading
              right={
                <span className="text-overline" style={{ color: accent }}>
                  {sportDef.label}
                </span>
              }
            >
              L&apos;évènement
            </SectionHeading>
            <div className="bg-[#111216] border border-white/10 rounded-xl p-5 md:p-8 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full" style={{ background: accent }} />
              <p className="font-body text-base md:text-lg text-zinc-300 whitespace-pre-line leading-relaxed">
                {champ.description}
              </p>
            </div>
          </section>
        )}

        {/* ---------- Stats générales ---------- */}
        <section className="max-w-5xl mx-auto w-full px-4 md:px-8 py-12 md:py-16" style={secStyle("stats")}>
          <SectionHeading
            right={
              champ.season ? (
                <span className="text-overline" style={{ color: accent }}>
                  Saison {champ.season}
                </span>
              ) : null
            }
          >
            Statistiques générales
          </SectionHeading>
          <div
            className={`grid grid-cols-2 gap-3 md:gap-4 ${
              visibleStats.length <= 2
                ? "md:grid-cols-2"
                : visibleStats.length === 3
                  ? "md:grid-cols-3"
                  : "md:grid-cols-4"
            }`}
            data-testid="championship-stats"
          >
            {visibleStats.map((s) => (
              <StatBox key={s.key} label={s.label} value={champ.stats?.[s.key] ?? 0} color={accent} />
            ))}
          </div>
        </section>

        {/* ---------- Classement final ---------- */}
        {standings.length > 0 && (
          <section className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-12 md:pb-16" style={secStyle("standings")}>
            <SectionHeading
              right={
                <ShareStoryButton
                  filename={`classement-${champ.slug}`}
                  card={{
                    title: champ.name,
                    subtitle: champ.subtitle,
                    kicker: terms.finalStandings || "Classement",
                    accent,
                    rows: standings.map((t, i) => ({
                      position: i + 1,
                      name: t.name,
                      logo: t.logo_url,
                      color: t.primary_color,
                      value: t.points ? `${t.points} pts` : undefined,
                    })),
                    footer: `${window.location.host}/championship/${champ.slug}`,
                  }}
                />
              }
            >
              {terms.finalStandings}
            </SectionHeading>
            <div
              className="bg-[#111216] border border-white/10 rounded-xl overflow-hidden"
              data-testid="championship-standings"
            >
              <div
                className="grid gap-1 sm:gap-2 px-2 sm:px-5 py-3 border-b border-white/10 text-[9px] sm:text-[11px] font-display font-bold uppercase tracking-[0.18em] text-zinc-500"
                style={{
                  gridTemplateColumns: `28px minmax(0,1fr) ${sportDef.standings_columns.map(() => "32px").join(" ")}`,
                }}
              >
                <div>#</div>
                <div>{terms.entity}</div>
                {sportDef.standings_columns.map((c, i) => (
                  <div
                    key={c.key}
                    className="text-right"
                    style={i === sportDef.standings_columns.length - 1 ? { color: accent } : {}}
                  >
                    {c.label}
                  </div>
                ))}
              </div>
              {standings.map((team, i) => {
                const linkTo = team.player_id
                  ? `/player/${team.player_id}`
                  : team.team_id
                    ? `/team/${team.team_id}`
                    : null;
                const RowTag = linkTo ? Link : "div";
                const rowProps = linkTo ? { to: linkTo } : {};
                return (
                  <RowTag
                    key={team.id || i}
                    {...rowProps}
                    className={`grid gap-1 sm:gap-2 px-2 sm:px-5 py-3 sm:py-4 border-b border-white/5 last:border-0 items-center transition-colors ${linkTo ? "hover:bg-white/[0.06] cursor-pointer" : "hover:bg-white/[0.02]"}`}
                    style={{
                      gridTemplateColumns: `28px minmax(0,1fr) ${sportDef.standings_columns.map(() => "32px").join(" ")}`,
                    }}
                    data-testid={`champ-standing-row-${i}`}
                  >
                    <div
                      className="font-display text-base sm:text-xl font-bold"
                      style={{ color: i < 3 ? accent : "#71717A" }}
                    >
                      {i + 1}
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <TeamBadge name={team.name} logoUrl={team.logo_url} color={team.primary_color} size="sm" />
                      <div className="font-display uppercase tracking-tight font-semibold text-[11px] sm:text-base leading-tight min-w-0">
                        {team.name}
                      </div>
                    </div>
                    {sportDef.standings_columns.map((c, idx) => {
                      const value = team[c.key] ?? team.extra?.[c.key] ?? 0;
                      const isLast = idx === sportDef.standings_columns.length - 1;
                      return (
                        <div
                          key={c.key}
                          className={`text-right font-display tabular-nums ${isLast ? "font-black text-base sm:text-lg" : "text-zinc-400"}`}
                          style={isLast ? { color: accent } : {}}
                        >
                          {value}
                        </div>
                      );
                    })}
                  </RowTag>
                );
              })}
            </div>
          </section>
        )}

        {/* ---------- Équipes participantes ---------- */}
        {standings.length > 0 && (
          <section
            className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-12 md:pb-16"
            data-testid="championship-teams"
            style={secStyle("teams")}
          >
            <SectionHeading
              right={
                <span className="text-overline" style={{ color: accent }}>
                  <Shield className="w-3 h-3 inline mr-1" /> {standings.length} {terms.countLabel}
                </span>
              }
            >
              {terms.participants}
            </SectionHeading>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {standings.map((t, i) => {
                const cardTo = t.player_id
                  ? `/player/${t.player_id}`
                  : t.team_id
                    ? `/team/${t.team_id}`
                    : null;
                const CardTag = cardTo ? Link : "div";
                const cardProps = cardTo ? { to: cardTo } : {};
                return (
                  <CardTag
                    key={t.id || i}
                    {...cardProps}
                    className={`flex items-center gap-3 bg-[#111216] border border-white/10 rounded-xl p-3 transition-colors ${cardTo ? "hover:border-[#D4AF37]/60 cursor-pointer" : ""}`}
                    data-testid={`participating-team-${i}`}
                  >
                    <TeamBadge name={t.name} logoUrl={t.logo_url} color={t.primary_color} />
                    <div className="font-display uppercase font-bold text-xs sm:text-sm tracking-tight leading-tight min-w-0">
                      {t.name}
                    </div>
                  </CardTag>
                );
              })}
            </div>
          </section>
        )}

        {/* ---------- Joueurs ---------- */}
        {(players.length > 0 || (champ.guest_players || []).length > 0) && (
          <section
            className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-12 md:pb-16"
            data-testid="championship-players"
            style={secStyle("players")}
          >
            <SectionHeading
              right={
                <span className="text-overline" style={{ color: accent }}>
                  <Users className="w-3 h-3 inline mr-1" />
                  {players.length + (champ.guest_players || []).length}{" "}
                  {terms.individual ? terms.countLabel : "joueurs"}
                </span>
              }
            >
              {terms.individual ? terms.entities : "Joueurs"}
            </SectionHeading>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {players.map((p) => (
                <Link
                  key={p.id}
                  to={`/player/${p.slug}`}
                  data-testid={`champ-player-${p.slug}`}
                  className="flex items-center gap-3 bg-[#111216] border border-white/10 rounded-xl p-3 hover:border-[#D4AF37]/60 transition-colors"
                >
                  <div className="w-10 h-10 rounded-full overflow-hidden border border-white/10 shrink-0">
                    {p.hero_image_url ? (
                      <img src={p.hero_image_url} alt={p.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-[#0A0A0A] flex items-center justify-center font-display text-zinc-600">
                        {p.name?.[0]}
                      </div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display uppercase font-bold text-xs sm:text-sm truncate">{p.name}</div>
                    <div className="text-[9px] uppercase tracking-[0.14em] text-zinc-500 truncate">
                      {p.club_name || p.position}
                    </div>
                  </div>
                </Link>
              ))}
              {(champ.guest_players || []).map((g, i) => (
                <div
                  key={g.id || i}
                  className="flex items-center gap-3 bg-[#111216] border border-white/10 rounded-xl p-3"
                  data-testid={`champ-guest-player-${i}`}
                >
                  <div className="w-10 h-10 rounded-full bg-[#0A0A0A] border border-white/10 flex items-center justify-center font-display text-zinc-600 shrink-0">
                    {g.name?.[0]}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display uppercase font-bold text-xs sm:text-sm truncate">{g.name}</div>
                    <div className="text-[9px] uppercase tracking-[0.14em] text-zinc-500 truncate">
                      {[g.team_name, g.position].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ---------- Récompenses individuelles ---------- */}
        {(champ.awards || []).length > 0 && (
          <section
            className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-12 md:pb-16"
            data-testid="championship-awards"
            style={secStyle("awards")}
          >
            <SectionHeading
              right={
                <span className="text-overline" style={{ color: accent }}>
                  <Award className="w-3 h-3 inline mr-1" /> {champ.awards.length}
                </span>
              }
            >
              Récompenses individuelles
            </SectionHeading>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
              {champ.awards.map((a, i) => {
                const linked = a.player_id ? playerById[a.player_id] : null;
                const Wrapper = linked ? Link : "div";
                const wrapperProps = linked ? { to: `/player/${linked.slug}` } : {};
                return (
                  <Wrapper
                    key={a.id || i}
                    {...wrapperProps}
                    className={`relative overflow-hidden bg-[#111216] border border-white/10 rounded-xl p-5 ${linked ? "hover:border-[#D4AF37]/60 transition-colors cursor-pointer" : ""}`}
                    data-testid={`champ-award-${i}`}
                  >
                    <div
                      className="absolute -top-6 -right-6 w-20 h-20 rounded-full opacity-20"
                      style={{ background: `radial-gradient(circle, ${accent} 0%, transparent 70%)` }}
                    />
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center mb-3 border"
                      style={{ background: `${accent}1A`, borderColor: `${accent}55` }}
                    >
                      <Award className="w-5 h-5" style={{ color: accent }} />
                    </div>
                    <div
                      className="text-[10px] uppercase tracking-[0.25em] font-bold mb-1"
                      style={{ color: accent }}
                    >
                      {a.title}
                    </div>
                    <div className="font-display text-xl font-black uppercase tracking-tight leading-tight">
                      {linked?.name || a.player_name}
                    </div>
                    {(a.team_name || linked?.club_name) && (
                      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-1">
                        {a.team_name || linked?.club_name}
                      </div>
                    )}
                  </Wrapper>
                );
              })}
            </div>
          </section>
        )}

        {/* ---------- Récompenses d'équipe ---------- */}
        {(champ.team_awards || []).filter((a) => (a.title || "").trim() || (a.team_name || "").trim()).length > 0 && (
          <section
            className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-12 md:pb-16"
            data-testid="championship-team-awards"
            style={secStyle("team_awards")}
          >
            <SectionHeading
              right={
                <span className="text-overline" style={{ color: accent }}>
                  <Handshake className="w-3 h-3 inline mr-1" />{" "}
                  {(champ.team_awards || []).filter((a) => (a.title || "").trim() || (a.team_name || "").trim()).length}
                </span>
              }
            >
              Récompenses d'équipe
            </SectionHeading>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
              {champ.team_awards
                .filter((a) => (a.title || "").trim() || (a.team_name || "").trim())
                .map((a, i) => (
                  <div
                    key={a.id || i}
                    className="relative overflow-hidden bg-[#111216] border border-white/10 rounded-xl p-3.5 flex items-center gap-3.5"
                    data-testid={`champ-team-award-${i}`}
                  >
                    <div
                      className="absolute -top-6 -right-6 w-20 h-20 rounded-full opacity-20 pointer-events-none"
                      style={{ background: `radial-gradient(circle, ${accent} 0%, transparent 70%)` }}
                    />
                    <div
                      className="w-14 h-14 rounded-full flex items-center justify-center border shrink-0 overflow-hidden"
                      style={{ background: `${accent}12`, borderColor: `${accent}45` }}
                    >
                      {a.photo_url ? (
                        <img
                          src={transparentLogoUrl(a.photo_url)}
                          alt={a.team_name}
                          className="w-full h-full object-contain p-1.5"
                        />
                      ) : (
                        <Handshake className="w-5 h-5" style={{ color: accent }} />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: accent }}>
                        {a.title}
                      </div>
                      <div className="font-display text-lg font-black uppercase tracking-tight leading-tight truncate">
                        {a.team_name}
                      </div>
                      {a.note && (
                        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-0.5 truncate">{a.note}</div>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </section>
        )}

        {/* ---------- Meilleurs moments ---------- */}
        {(champ.best_moments || []).length > 0 && (
          <section
            className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-12 md:pb-16"
            data-testid="championship-moments"
            style={secStyle("moments")}
          >
            <SectionHeading
              right={
                <span className="text-overline" style={{ color: accent }}>
                  <Sparkles className="w-3 h-3 inline mr-1" /> {champ.best_moments.length}
                </span>
              }
            >
              Meilleurs moments
            </SectionHeading>
            <div className="grid md:grid-cols-2 gap-5">
              {champ.best_moments.map((h) => (
                <div key={h.id} data-testid={`champ-moment-${h.id}`}>
                  <HighlightEmbed h={h} />
                  {h.title && (
                    <div className="mt-3 font-display uppercase tracking-wider text-zinc-300">
                      {h.title}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ---------- Vidéos ---------- */}
        {(champ.highlights || []).length > 0 && (
          <section
            className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-12 md:pb-16"
            data-testid="championship-videos"
            style={secStyle("videos")}
          >
            <SectionHeading
              right={
                <span className="text-overline" style={{ color: accent }}>
                  <Video className="w-3 h-3 inline mr-1" /> {champ.highlights.length} vidéo
                  {champ.highlights.length > 1 ? "s" : ""}
                </span>
              }
            >
              Vidéos
            </SectionHeading>
            <div className="grid md:grid-cols-2 gap-5">
              {champ.highlights.map((h) => (
                <div key={h.id} data-testid={`champ-highlight-${h.id}`}>
                  <HighlightEmbed h={h} />
                  {h.title && (
                    <div className="mt-3 font-display uppercase tracking-wider text-zinc-300">
                      {h.title}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ---------- Photos ---------- */}
        {(champ.gallery || []).length > 0 && (
          <section
            className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-12 md:pb-16"
            data-testid="championship-gallery"
            style={secStyle("gallery")}
          >
            <SectionHeading>Photos</SectionHeading>
            <div className="hidden md:grid grid-cols-3 gap-4">
              {champ.gallery.map((g, i) => (
                <motion.button
                  type="button"
                  key={g.id}
                  onClick={() => setLightboxIndex(i)}
                  initial={{ opacity: 0, scale: 0.96 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, ease, delay: (i % 6) * 0.04 }}
                  className="aspect-square rounded-2xl overflow-hidden border border-white/10 group relative shine-on-hover focus:outline-none focus:ring-2 focus:ring-[color:var(--gold)]"
                  data-testid={`champ-gallery-${g.id}`}
                >
                  <img
                    src={g.url}
                    alt={g.caption || ""}
                    className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                  />
                </motion.button>
              ))}
            </div>
            <div className="md:hidden flex gap-3 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2 -mx-4 px-4">
              {champ.gallery.map((g, i) => (
                <button
                  type="button"
                  key={g.id}
                  onClick={() => setLightboxIndex(i)}
                  className="snap-start shrink-0 w-[78%] aspect-square rounded-2xl overflow-hidden border border-white/10"
                  data-testid={`champ-gallery-mobile-${g.id}`}
                >
                  <img src={g.url} alt={g.caption || ""} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </section>
        )}
        <ImmersiveLightbox
          items={champ.gallery || []}
          openIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          testId="championship-gallery-lightbox"
        />

        {/* ---------- Partenaires ---------- */}
        {(champ.sponsors || []).length > 0 && (
          <section
            className="max-w-5xl mx-auto w-full px-4 md:px-8 pb-12 md:pb-16"
            data-testid="championship-sponsors"
            style={secStyle("sponsors")}
          >
            <SectionHeading
              right={
                <span className="text-overline" style={{ color: accent }}>
                  <Handshake className="w-3 h-3 inline mr-1" /> {champ.sponsors.length}
                </span>
              }
            >
              Partenaires
            </SectionHeading>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {champ.sponsors.map((s, i) => {
                const Tag = s.website ? "a" : "div";
                const props = s.website
                  ? { href: s.website, target: "_blank", rel: "noopener noreferrer" }
                  : {};
                return (
                  <Tag
                    key={s.id || i}
                    {...props}
                    className={`flex flex-col items-center justify-center gap-3 bg-[#111216] border border-white/10 rounded-xl p-5 min-h-[120px] ${s.website ? "hover:border-[#D4AF37]/60 transition-colors" : ""}`}
                    data-testid={`champ-sponsor-${i}`}
                  >
                    {s.logo_url ? (
                      <img
                        src={s.logo_url}
                        alt={s.name}
                        className="max-h-14 max-w-[80%] object-contain"
                        style={{ mixBlendMode: "screen" }}
                      />
                    ) : (
                      <Handshake className="w-8 h-8 text-zinc-600" />
                    )}
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-bold text-center">
                      {s.name}
                    </div>
                  </Tag>
                );
              })}
            </div>
          </section>
        )}

        {/* QR retiré de la page publique (réservé à l'administration) */}

        <footer className="border-t border-white/5 py-8 text-center" style={{ order: 99 }}>
          {user && (
            <Link
              to="/championships"
              className="inline-flex items-center gap-2 text-zinc-500 hover:text-white transition-colors text-xs uppercase tracking-[0.25em] font-body"
            >
              <ArrowLeft className="w-3 h-3" /> Tous les championnats
            </Link>
          )}
          <div className="mt-4 text-[10px] uppercase tracking-[0.3em] text-zinc-700">
            Laser Tech · Création · Genève
          </div>
        </footer>
      </div>
    </ScanReveal>
  );
}
